import { MapRow as PredefinedMapRow } from "../Game Map/MapRow";
import { MapCell as PredefinedMapCell, eCellType } from "../Game Map/MapCell";
import { ViewCellMap } from "./ViewCellMap";
import { ViewCellMapCell } from "./ViewCellMapCell";
import { GameWorld } from "../GameWorld";
import { TileBase } from "../Ground Tiles/TileBase";
import { Player } from "../Player/Player";
import { MapRowShiftSettings, eMapRowSettings } from "../Game Map/MapRowSettings";
import { Game, eSfxSprite } from "../../../Game";
import { TIMER } from "../../Gameplay";
import { ObstacleManager } from "../Obstacles/ObstacleManager";
import { ObstacleMapData } from "../Obstacles/ObstacleMapData";
import { BonusManager } from "../Bonuses/BonusManager";
import { PlatformManager } from "../Platforms/PlatformManager";
import { MapRowWrapper } from "../Game Map/PathGenerator";
import { SoundManager } from "../../../Utils/SoundManager";


export const enum eRowState {
    /**
     * Radka je deaktivovana
     */
    released = -1,
    /**
     * Probiha animace aktivace radky
     */
    activation,
    /**
     * Radka je necinna
     */
    idle,
    /**
     * Probiha zpracovani radky podle jejiho typu
     */
    update,
    /**
     * Probiha deaktivace radky
     */
    deactivation,
}



const enum eRowType {
    basic,
    shifting,
}



export class ViewCellMapRow {
    private static readonly ACTIVATION_OFFSET_Y = 4;

    /**
     * GraphNode, ktery obsahuje ostatni entity na radce (dlazdice, prekazky, ...)
     */
    get graphNode(): pc.GraphNode { return this._graphNode; }
    private _graphNode: pc.GraphNode;

    /**
     * Aktualni pozice radky ve scene
     */
    private _pos: pc.Vec3;

    /**
     * (RO) Pozice radky na ose Z v hernim svete
     */
    get z(): number { return this._pos.z; }

    /**
     * (RO) Data radky v preddefinovane mape herniho sveta
     */
    get data(): PredefinedMapRow { return this._data; }
    private _data: PredefinedMapRow;

    /**
     * (RO) Index radky v ramci vygenerovane cesty
     */
    get rowId(): number { return this._rowId; }
    private _rowId: number;

    /**
     * Pocet aktivnich prekazek na radce
     */
    private _obstacleCnt: number;

    /**
     * Pocet aktivnich bonusu na radce
     */
    private _bonusCnt: number;

    /**
     * Pole bunek
     */
    private _cells: ViewCellMapCell[];

    /**
     * Typ radky
     */
    private _type: eRowType;

    /**
     * (RO) Aktualni stav
     */
    get state(): eRowState { return this._state; }
    private _state: eRowState;

    /**
     * Casovac
     */
    private _stateTimer: number;

    /**
     * True, pokud zacal posun radky
     */
    private _shiftStarted: boolean;

    private static _TMP_CELLDATA: PredefinedMapCell = new PredefinedMapCell();

    /**
     * Process funkce pro jednotlive typy radek
     */
    private static _TYPE_PROCESS_FNC: (() => boolean)[];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        if (!ViewCellMapRow._TYPE_PROCESS_FNC) {
            ViewCellMapRow._TYPE_PROCESS_FNC = [];
            ViewCellMapRow._TYPE_PROCESS_FNC[eRowType.shifting] = this.processShifting;
        }

        this._state = eRowState.released;

        // GraphNode
        this._graphNode = new pc.GraphNode();
        this._graphNode.enabled = false;
        Game.app.root.addChild(this._graphNode);

        this._pos = new pc.Vec3();

        // Bunky
        this._cells = [];

        for (let i = 0; i < PredefinedMapRow.WIDTH; i++)
            this._cells.push(undefined);
    }

    //-------------------------------------------------------------------------
    /**
     * Inicializace radky.
     * @param rowId 
     * @param data
     * @param animate True, pokud se ma animovat aktivace radky
     * @returns True, pokud je u radky potreba volat update()
     */
    //-------------------------------------------------------------------------
    init(rowId: number, data: MapRowWrapper, animate: boolean): boolean {
        this._rowId = rowId;
        this._data = data.row;
        this._type = eRowType.basic;

        // Nastaveni radky
        this._pos.set(GameWorld.X, GameWorld.GROUND_Y, GameWorld.Z - (TileBase.SIZE / 2) - (rowId * TileBase.SIZE));

        if (this._data && this._data.settings) {
            if (this._data.settings.type == eMapRowSettings.shift) {
                this._pos.x += (<MapRowShiftSettings>this._data.settings).startPos;
                this._type = eRowType.shifting;
                this._shiftStarted = false;
            }
        }

        if (animate) {
            this._pos.y += ViewCellMapRow.ACTIVATION_OFFSET_Y;
            this._state = eRowState.activation;
            this._stateTimer = TIMER.time;
        }
        else {
            this._state = (this._type == eRowType.basic ? eRowState.idle : eRowState.update);
        }

        this._graphNode.setPosition(this._pos);
        this._graphNode.enabled = true;

        // Inicializovat bunky radky
        let map = ViewCellMap.instance;
        let platformCellId: number = -1;

        this._bonusCnt = 0;

        if (this._data) {
            for (let cellId = 0; cellId < PredefinedMapRow.WIDTH; cellId++) {
                let cellData = this._data.getCell(cellId, ViewCellMapRow._TMP_CELLDATA);
                let cell: ViewCellMapCell;

                if (cellData) {
                    cell = map.getFreeCell();
                    cell.init(cellId, this, cellData);
                    this._cells[cellId] = cell;

                    // Bonus
                    if (cellData.bonusType >= 0) {
                        BonusManager.instance.initBonus(cellData.bonusType, cellData.bonusGroupMark, cellId, this, data.pathEntryId);
                        this._bonusCnt++;
                    }

                    // Pojizdna plosina
                    if (platformCellId < 0) {
                        if (cell.type == eCellType.platform)
                            platformCellId = cellId;
                    }
                }
                else {
                    this._cells[cellId] = undefined;
                }

                if (platformCellId >= 0 && (!cellData || cell.type != eCellType.platform)) {
                    // Vytvorit pojizdnou plosinu
                    PlatformManager.instance.initPlatform(cellId - platformCellId, platformCellId, this);
                    platformCellId = -1;
                }
            }

            if (platformCellId >= 0)
                // Vytvorit pojizdnou plosinu
                PlatformManager.instance.initPlatform(PredefinedMapRow.WIDTH - platformCellId, platformCellId, this);

            // Prekazky
            this._obstacleCnt = 0;

            if (this._data.obstacles) {
                let mng = ObstacleManager.instance;

                this._data.obstacles.forEach((obsData: ObstacleMapData) => {
                    mng.initObstacle(obsData, this);
                });

                this._obstacleCnt = this._data.obstacles.length;
            }
        }
        else {
            let cellId = PredefinedMapRow.WIDTH;

            while (cellId-- != 0)
                this._cells[cellId] = undefined;
        }

        return (this._state != eRowState.idle);
    }

    //-------------------------------------------------------------------------
    /**
     * Kompletni uvolneni radky (bez ohledu na aktivni prekazky na radce)
     */
    //-------------------------------------------------------------------------
    release(): void {
        this.releaseCells();
        this.releaseBase();
    }

    //-------------------------------------------------------------------------
    /**
     * Deaktivace radky.
     * Nejprve uvolni bunky radky (pokud jiz nebyly uvolneny) a pak uvolni zbytek, pokud to je mozne. Pokud neni mozne radku uvolnit, tak vraci false.
     * @returns True, pokud radka byla uvolnena
     */
    //-------------------------------------------------------------------------
    deactivate(): boolean {
        if (this._state != eRowState.deactivation) {
            this._state = eRowState.deactivation;
            this.releaseCells();
        }

        if (this._obstacleCnt == 0 && this._bonusCnt == 0) {
            this.releaseBase();
            return true;
        }

        return false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if (this._state == eRowState.activation)
            return this.processActivation();

        if (this._state != eRowState.update)
            return false;

        return ViewCellMapRow._TYPE_PROCESS_FNC[this._type].call(this);
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci bunku radky nebo undefined, pokud je bunka prazdna
     * @param colId Index sloupce, pro ktery chceme bunku
     */
    //-------------------------------------------------------------------------
    getCell(colId: number): ViewCellMapCell {
        return this._cells[colId];
    }

    //-------------------------------------------------------------------------
    /**
     * Snizi pocet aktivnich prekazek na radce.
     */
    //-------------------------------------------------------------------------
    decActObstacles(): void {
        this._obstacleCnt--;
    }

    //-------------------------------------------------------------------------
    /**
     * Snizi pocet aktivnich bonusu na radce.
     */
    //-------------------------------------------------------------------------
    decActBonuses(): void {
        this._bonusCnt--;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processActivation(): boolean {
        let progress = (TIMER.time - this._stateTimer) / 1000;

        if (progress > 1)
            progress = 1;

        this._pos.y = GameWorld.GROUND_Y + ViewCellMapRow.ACTIVATION_OFFSET_Y - (ViewCellMapRow.ACTIVATION_OFFSET_Y * pc.BackOut(progress));
        this._graphNode.setPosition(this._pos);

        if (progress == 1) {
            if (this._type == eRowType.basic) {
                this._state = eRowState.idle;
                return false;
            }

            this._state = eRowState.update;
        }

        return true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processShifting(): boolean {
        let settings = <MapRowShiftSettings>this._data.settings;
        let rowPos = GameWorld.Z - this._rowId * TileBase.SIZE;
        let shiftStartPos = rowPos + settings.shiftStartPlOffset;
        let plPos = Player.instance.cameraOrigin.z;

        if (plPos < shiftStartPos) {
            if (!this._shiftStarted) {
                this._shiftStarted = true;
                SoundManager.playSound(Player.instance.entity, eSfxSprite.slide);
            }

            // Vypocitat pozici v presunu
            let shiftEndPos = rowPos + settings.shiftEndPlOffset;
            let progress = (shiftStartPos - plPos) / (shiftStartPos - shiftEndPos);

            if (progress > 1)
                progress = 1;

            // Upravit pozici radky
            this._pos.x = GameWorld.X + settings.startPos + (settings.targetPos - settings.startPos) * settings.shiftEase(progress);
            this._graphNode.setPosition(this._pos);

            if (progress == 1)
                return false;
        }

        return true;
    }

    //-------------------------------------------------------------------------
    /**
     * Uvolneni bunek radky
     */
    //-------------------------------------------------------------------------
    private releaseCells(): void {
        let cellId = PredefinedMapRow.WIDTH;
        let map = ViewCellMap.instance;

        while (cellId-- != 0) {
            if (this._cells[cellId]) {
                map.releaseCell(this._cells[cellId]);
                this._cells[cellId] = undefined;
            }
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Uvolni zbyvajici data
     */
    //-------------------------------------------------------------------------
    private releaseBase(): void {
        this._graphNode.enabled = false;
        this._data = undefined;
        this._rowId = -1;
        this._obstacleCnt = 0;
        this._bonusCnt = 0;
        this._state = eRowState.released;
    }
}